/**
 * Courses service — Firestore CRUD for the `courses` collection (the
 * admin-curated course catalogue that students enrol into).
 *
 * A course document:
 *   {
 *     name,                     // display title, e.g. "Python Level-1"
 *     description,              // one-paragraph blurb shown on the student profile
 *     level,                    // 'Beginner' | 'Intermediate' | 'Advanced' | ''
 *     subject,                  // links to admin/constants SUBJECTS — the same
 *                               // string `classes.subject` uses, so a course's
 *                               // sessions can be derived by matching subject.
 *     modules: string[],        // ordered module titles — powers the enrolment's
 *                               // "current module/lesson" picker and the syllabus.
 *     active,                   // bool — hide retired courses from the enrol picker
 *     createdAt, updatedAt      // server timestamps
 *   }
 *
 * Distinct from the loose SUBJECTS string list in admin/constants: a course
 * carries the description + syllabus the SUBJECTS strings can't. `subject` keeps
 * the two aligned so existing classes/resources continue to work unchanged.
 */
import firestoreService, { orderBy, where } from './firestore.service';

const COURSES = 'courses';

/** Whitelist the fields we persist so stray form state never leaks in. */
function sanitize(input = {}) {
  return {
    name: (input.name || '').trim(),
    description: (input.description || '').trim(),
    level: input.level || '',
    subject: input.subject || '',
    modules: Array.isArray(input.modules)
      ? input.modules.map((m) => (m || '').trim()).filter(Boolean)
      : [],
    active: input.active !== false, // default active
  };
}

export const coursesService = {
  /** Realtime subscription to all courses, alphabetical (catalogue order). */
  subscribe(onData, onError) {
    return firestoreService.subscribeCollection(
      COURSES,
      [orderBy('name', 'asc')],
      onData,
      onError
    );
  },

  list() {
    return firestoreService.getCollection(COURSES, [orderBy('name', 'asc')]);
  },

  /** Server-side total count (aggregation — no document downloads). */
  count() {
    return firestoreService.count(COURSES);
  },

  /**
   * Realtime subscription to only the active courses. Filtered server-side by
   * `active == true`; sorted by name client-side to avoid a composite index.
   * Powers the "pick a course" dropdown in the enrolment modal.
   */
  subscribeActive(onData, onError) {
    return firestoreService.subscribeCollection(
      COURSES,
      [where('active', '==', true)],
      (rows) => onData([...rows].sort((a, b) => (a.name || '').localeCompare(b.name || ''))),
      onError
    );
  },

  get(id) {
    return firestoreService.getDocument(COURSES, id);
  },

  create(data) {
    return firestoreService.addDocument(COURSES, sanitize(data));
  },

  update(id, data) {
    return firestoreService.updateDocument(COURSES, id, sanitize(data));
  },

  remove(id) {
    return firestoreService.deleteDocument(COURSES, id);
  },
};

export default coursesService;
